import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { Field } from 'react-final-form';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardJump from '@swf/core/hooks/useWizardJump';

import { CashierFlowType } from '../../../../types/Cashier';
import type { HelpSection } from '../../../../types/Cashier';
import { CashoutStatus } from '../../../../types/status';
import {
  filteredUnverifiedOptions,
  getPaySystemName,
  getPaymentVerificationPath
} from '../../../../utils/cashier';
import Container from '../../../Container/Container';
import HelpSectionContent from '../HelpSectionContent/HelpSectionContent';

import {
  TXTWCL_Verify_your_payment_method,
  TXTWCL_Verify_your_payment_methods,
  TXTWCL_Verifying_your_payment_method__ELL_1,
  TXTWCL_Verifying_your_payment_methods_ELL,
  TXTWCL_Why_verify_your_payment_method_ELL
} from './PaymentVerificationPage.messages.json';
import { documentsContainer } from './PaymentVerificationPage.scss';
import CollapsiblePayment from './components/CollapsiblePayment/CollapsiblePayment';
import DocumentRequirements from './components/DocumentRequirements/DocumentRequirements';
import {
  defaultMessages,
  translationMap
} from './components/DocumentRequirements/utils/translationUtils';
import DocumentsUpload from './components/DocumentsUpload/DocumentsUpload';

const PaymentVerificationPage: React.FC = () => {
  const { jumpBack } = useWizardJump();
  const {
    data: {
      cashierFlow,
      overCredit,
      rollback,
      paySystems,
      unverifiedOptions = [],
      selectedOverCreditRecord,
      withdrawResult
    },
    values: { deposit }
  } = useWizardContext();

  let methodType;
  if (cashierFlow === CashierFlowType.FirstDeposit) {
    methodType = deposit.type;
  } else {
    if ((rollback || overCredit) && unverifiedOptions.length) {
      methodType = unverifiedOptions[0].cardType;
    }
  }

  if (!methodType) return null;

  // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
  const { subtitle } = translationMap[methodType] || defaultMessages;

  const withdrawSubtitle =
    unverifiedOptions.length === 1
      ? TXTWCL_Verifying_your_payment_method__ELL_1
      : TXTWCL_Verifying_your_payment_methods_ELL;

  let i18nSystemName = '';
  if (cashierFlow === CashierFlowType.FirstDeposit) {
    i18nSystemName = getPaySystemName(paySystems, methodType);
  } else {
    if ((rollback || overCredit) && unverifiedOptions.length) {
      i18nSystemName = unverifiedOptions[0].paySystemName;
    }
  }

  const helpSection: HelpSection = {
    title: TXTWCL_Why_verify_your_payment_method_ELL,
    body: <HelpSectionContent />
  };
  const goBack = () => jumpBack(getPaymentVerificationPath(cashierFlow));

  const filteredOptions = filteredUnverifiedOptions(unverifiedOptions, i18nSystemName);

  const isSingleUnverifiedMethod =
    cashierFlow === CashierFlowType.FirstDeposit ||
    unverifiedOptions.length === 1 ||
    (filteredOptions.length > 1 && unverifiedOptions.length === filteredOptions.length);

  const seenCardTypes = new Set();
  const uniqueUnverifiedOptions = unverifiedOptions.filter((rollback) => {
    if (!seenCardTypes.has(rollback.cardType)) {
      seenCardTypes.add(rollback.cardType);
      return true;
    }
    return false;
  });

  return (
    <Container
      headerProps={{
        title: <Translation {...TXTWCL_Verify_your_payment_method} />,
        buttons: {
          back:
            cashierFlow === CashierFlowType.FirstWithdraw &&
            selectedOverCreditRecord?.ref !== '' &&
            withdrawResult?.status === CashoutStatus.NOT_VERIFIED_ACCOUNT
              ? false
              : true,
          ...(cashierFlow === CashierFlowType.FirstDeposit && { onBackClick: goBack })
        }
      }}
      page={PaymentVerificationPage}
      pageTopTitle={
        unverifiedOptions.length === 1 ? (
          <Translation {...TXTWCL_Verify_your_payment_method} />
        ) : (
          <Translation {...TXTWCL_Verify_your_payment_methods} />
        )
      }
      pageTopSubtitle={
        cashierFlow === CashierFlowType.FirstDeposit ? (
          <Translation {...subtitle} />
        ) : (
          <Translation {...withdrawSubtitle} />
        )
      }
      helpSupportResolver
      showFooter
      helpSection={helpSection}
    >
      {isSingleUnverifiedMethod ? (
        <DocumentRequirements type={methodType} i18nSystemName={i18nSystemName} />
      ) : (
        uniqueUnverifiedOptions.map((rollbackOption) => (
          <CollapsiblePayment
            key={rollbackOption.cardType}
            type={rollbackOption.cardType}
            i18nSystemName={rollbackOption.paySystemName}
          >
            <DocumentRequirements
              className={documentsContainer}
              type={rollbackOption.cardType}
              i18nSystemName={rollbackOption.paySystemName}
            />
          </CollapsiblePayment>
        ))
      )}
      <Field name="paymentVerification.files" initialValue={[]}>
        {({ input: { onChange, value } }) => (
          <DocumentsUpload existingFiles={value} onChange={onChange} />
        )}
      </Field>
    </Container>
  );
};

PaymentVerificationPage.displayName = 'PaymentVerificationPage';

export default PaymentVerificationPage;
